import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Landing.css';

const FEATURES = [
  {
    icon: '🤖',
    title: 'Custom AI Chatbots',
    desc: 'Build fully branded chatbots in minutes. Tailor the personality, tone, and expertise to match your business perfectly.',
  },
  {
    icon: '📚',
    title: 'Knowledge Base Training',
    desc: 'Upload PDFs, docs, and text files. Your bot learns from your content and answers questions accurately.',
  },
  {
    icon: '⚡',
    title: 'Powered by Groq',
    desc: 'Ultra-fast responses using the latest LLMs. Your customers get answers in milliseconds, not seconds.',
  },
  {
    icon: '🌐',
    title: 'Embed Anywhere',
    desc: 'One script tag — that\'s all it takes. Drop a floating chat widget onto any website, landing page, or app.',
  },
  {
    icon: '🔍',
    title: 'Semantic Search',
    desc: 'Vector-powered RAG retrieval finds the most relevant knowledge even when the question is phrased differently.',
  },
  {
    icon: '📊',
    title: 'Usage Analytics',
    desc: 'Track conversations, message volume, and token usage. Know exactly how your bots are performing.',
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Create your chatbot',
    desc: 'Give it a name, write a system prompt, and choose how you want it to behave. No coding needed.',
  },
  {
    num: '02',
    title: 'Train it on your data',
    desc: 'Upload your documents, FAQs, product guides, or anything else. The bot learns your content instantly.',
  },
  {
    num: '03',
    title: 'Deploy & share',
    desc: 'Share a public link or embed a floating widget on your site. Your AI assistant is live in seconds.',
  },
];

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    color: 'plan-free',
    features: ['1 chatbot', '500 messages/month', '5 documents', 'Public chat link', 'Embed widget'],
    cta: 'Get started free',
    highlight: false,
  },
  {
    name: 'Starter',
    price: '$19',
    period: '/month',
    color: 'plan-starter',
    features: ['100 chatbots', '10,000 messages/month', '100 documents per bot', 'Priority support', 'Custom branding'],
    cta: 'Start with Starter',
    highlight: false,
  },
  {
    name: 'Pro',
    price: '$49',
    period: '/month',
    color: 'plan-pro',
    features: ['200 chatbots', '100,000 messages/month', '200 documents per bot', 'Advanced analytics', 'API access'],
    cta: 'Go Pro',
    highlight: true,
  },
  {
    name: 'Business',
    price: 'Custom',
    period: '',
    color: 'plan-business',
    features: ['Unlimited everything', 'Dedicated support', 'Custom integrations', 'SLA guarantee', 'White-label option'],
    cta: 'Contact us',
    highlight: false,
  },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav className={`landing-nav ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-inner">
        <Link to="/" className="nav-logo">
          <span className="logo-icon">✦</span>
          Solvi
        </Link>
        <div className="nav-links">
          <a href="#features">Features</a>
          <a href="#how-it-works">How it works</a>
          <a href="#pricing">Pricing</a>
        </div>
        <div className="nav-actions">
          {user ? (
            <Link to="/dashboard" className="btn-nav-primary">Go to Dashboard →</Link>
          ) : (
            <>
              <Link to="/login" className="btn-nav-ghost">Sign in</Link>
              <Link to="/register" className="btn-nav-primary">Get started free</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-bg">
        <div className="hero-orb orb-1" />
        <div className="hero-orb orb-2" />
        <div className="hero-orb orb-3" />
      </div>
      <div className="hero-inner">
        <div className="hero-badge">
          <span className="badge-dot" />
          Powered by Groq &amp; LLaMA 3.1
        </div>
        <h1 className="hero-title">
          Build AI chatbots that<br />
          <span className="hero-gradient">actually know your business</span>
        </h1>
        <p className="hero-subtitle">
          Create custom AI assistants trained on your documents in minutes.
          Embed them anywhere. No code. No limits.
        </p>
        <div className="hero-ctas">
          <Link to="/register" className="cta-primary">
            Start for free
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
          <a href="#how-it-works" className="cta-secondary">See how it works</a>
        </div>
        <div className="hero-stats">
          <div className="stat"><strong>60s</strong><span>to first chatbot</span></div>
          <div className="stat-divider" />
          <div className="stat"><strong>∞</strong><span>knowledge sources</span></div>
          <div className="stat-divider" />
          <div className="stat"><strong>1 line</strong><span>to embed</span></div>
        </div>
      </div>

      {/* Chat preview mockup */}
      <div className="hero-mockup">
        <div className="mockup-window">
          <div className="mockup-bar">
            <span /><span /><span />
            <div className="mockup-title">Solvi Chat</div>
          </div>
          <div className="mockup-body">
            <div className="mockup-msg bot">
              <div className="mockup-avatar">S</div>
              <div className="mockup-bubble">
                Hi! I'm your AI assistant. Ask me anything about our products or services.
              </div>
            </div>
            <div className="mockup-msg user">
              <div className="mockup-bubble user">
                What's included in the Pro plan?
              </div>
            </div>
            <div className="mockup-msg bot">
              <div className="mockup-avatar">S</div>
              <div className="mockup-bubble">
                The Pro plan gives you 200 chatbots, 100k messages/month, and 200 documents per bot — perfect for agencies and growing teams. ✨
              </div>
            </div>
            <div className="mockup-typing">
              <div className="mockup-avatar">S</div>
              <div className="typing-dots"><span/><span/><span/></div>
            </div>
          </div>
          <div className="mockup-input">
            <input readOnly placeholder="Ask me anything…" />
            <button>↑</button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="features" id="features">
      <div className="section-inner">
        <div className="section-label">Features</div>
        <h2 className="section-title">Everything you need to<br />deploy smarter support</h2>
        <p className="section-sub">From knowledge ingestion to live deployment — all in one platform.</p>
        <div className="features-grid">
          {FEATURES.map(f => (
            <div className="feature-card" key={f.title}>
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="how-it-works" id="how-it-works">
      <div className="section-inner">
        <div className="section-label">How it works</div>
        <h2 className="section-title">Live in three steps</h2>
        <div className="steps">
          {STEPS.map((s, i) => (
            <div className="step" key={s.num}>
              <div className="step-num">{s.num}</div>
              {i < STEPS.length - 1 && <div className="step-connector" />}
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const navigate = useNavigate();

  return (
    <section className="pricing" id="pricing">
      <div className="section-inner">
        <div className="section-label">Pricing</div>
        <h2 className="section-title">Simple, transparent pricing</h2>
        <p className="section-sub">Start free. Scale when you're ready. No hidden fees.</p>
        <div className="pricing-grid">
          {PLANS.map(p => (
            <div className={`pricing-card ${p.highlight ? 'highlight' : ''}`} key={p.name}>
              {p.highlight && <div className="popular-badge">Most Popular</div>}
              <div className={`pricing-plan-name ${p.color}`}>{p.name}</div>
              <div className="pricing-price">
                <span className="price-amount">{p.price}</span>
                <span className="price-period">{p.period}</span>
              </div>
              <ul className="pricing-features">
                {p.features.map(f => (
                  <li key={f}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                className={`pricing-cta ${p.highlight ? 'cta-highlight' : ''}`}
                onClick={() => navigate(p.name === 'Business' ? '#' : '/register')}
              >
                {p.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="cta-section">
      <div className="cta-orb" />
      <div className="section-inner cta-inner">
        <h2>Ready to build your first AI chatbot?</h2>
        <p>Join hundreds of businesses already using Solvi to automate their customer support.</p>
        <Link to="/register" className="cta-primary large">
          Get started for free
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </Link>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="landing-footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <Link to="/" className="nav-logo">
            <span className="logo-icon">✦</span> Solvi
          </Link>
          <p>AI chatbots trained on your business. Deployed in minutes.</p>
        </div>
        <div className="footer-links">
          <div>
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#how-it-works">How it works</a>
          </div>
          <div>
            <h4>Account</h4>
            <Link to="/login">Sign in</Link>
            <Link to="/register">Register</Link>
          </div>
          <div>
            <h4>Legal</h4>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/data-deletion">Data Deletion</Link>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Solvi. All rights reserved.</span>
        <span style={{marginLeft:'1.5rem'}}>
          <Link to="/privacy" style={{color:'inherit',marginRight:'.75rem'}}>Privacy</Link>
          <Link to="/terms" style={{color:'inherit'}}>Terms</Link>
        </span>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="landing">
      <Navbar />
      <Hero />
      <Features />
      <HowItWorks />
      <Pricing />
      <CTA />
      <Footer />
    </div>
  );
}
